import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import AnswerStyleDialog from '@/components/answerStyle/AnswerStyleDialog';
import useAnalytics from '@/hooks/useAnalytics';
import useAnswerStyleActions, { type AnswerStyleSource } from '@/hooks/useAnswerStyleActions';

interface AnswerStyleEditorContextValue {
  /**
   * Open the answer-style editor. `returnFocusTo` is focused when it closes —
   * for openers that unmount with it (a menu item), whose own focus restore
   * would otherwise land on the page body.
   */
  openAnswerStyleEditor: (source: AnswerStyleSource, returnFocusTo?: HTMLElement | null) => void;
  /** Record that the empty-chat invitation was on screen this session. */
  markInviteShown: () => void;
  /** Starting a chat past a shown invitation retires it (see the spec). */
  retireInviteOnChat: () => void;
}

const AnswerStyleEditorContext = createContext<AnswerStyleEditorContextValue | undefined>(
  undefined
);

/** Owns the one answer-style editor, so every entry point opens the same one. */
export function AnswerStyleEditorProvider({ children }: { children: React.ReactNode }) {
  const { track } = useAnalytics();
  const { dismissInvite } = useAnswerStyleActions();
  const [source, setSource] = useState<AnswerStyleSource | null>(null);
  const [openCount, setOpenCount] = useState(0);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const inviteShownRef = useRef(false);

  const openAnswerStyleEditor = useCallback(
    (next: AnswerStyleSource, returnFocusTo?: HTMLElement | null) => {
      returnFocusRef.current = returnFocusTo ?? null;
      track('Answer Style Editor Opened', { source: next });
      setOpenCount((count) => count + 1);
      setSource(next);
    },
    [track]
  );

  const markInviteShown = useCallback(() => {
    inviteShownRef.current = true;
  }, []);

  const retireInviteOnChat = useCallback(() => {
    if (!inviteShownRef.current) return;
    inviteShownRef.current = false;
    dismissInvite('invite', 'chat_started');
  }, [dismissInvite]);

  const value = useMemo(
    () => ({ openAnswerStyleEditor, markInviteShown, retireInviteOnChat }),
    [openAnswerStyleEditor, markInviteShown, retireInviteOnChat]
  );

  return (
    <AnswerStyleEditorContext.Provider value={value}>
      {children}
      {openCount > 0 && (
        <AnswerStyleDialog
          key={openCount}
          open={source !== null}
          source={source ?? 'composer'}
          onClose={() => setSource(null)}
          onExited={() => {
            const target = returnFocusRef.current;
            returnFocusRef.current = null;
            if (target?.isConnected) target.focus();
          }}
        />
      )}
    </AnswerStyleEditorContext.Provider>
  );
}

const NO_EDITOR: AnswerStyleEditorContextValue = {
  openAnswerStyleEditor: () => {},
  markInviteShown: () => {},
  retireInviteOnChat: () => {},
};

/** The editor controls; inert outside the provider (an isolated render or test). */
export function useAnswerStyleEditor(): AnswerStyleEditorContextValue {
  return useContext(AnswerStyleEditorContext) ?? NO_EDITOR;
}
